import axios from 'axios';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import getAuthSession from '../../services/auth';
import { Questionnaire, QuestionnaireResultArray } from '../../interfaces/questionnaire.interface';
import { FieldValues } from 'react-hook-form';
import { Domain } from '../../interfaces/domain.interface';

interface InitialQuestionnairesState {
  count: number;
  next?: string;
  previous?: string;
  questionnaire?: Questionnaire;
  results: Questionnaire[];
  deletedQuestionnaireId?: number;
  error?: any;
}

const initialState = {
  count: 0,
  next: undefined,
  previous: undefined,
  questionnaire: undefined,
  results: [],
  deletedQuestionnaireId: undefined,
  error: undefined,
} as InitialQuestionnairesState;

const baseUrl = process.env.REACT_APP_BASE_API;

interface FetchAllArgs {
  unpublished?: boolean;
  type?: string;
  limit?: number;
  offset?: number;
  parentsOnly?: boolean;
}

export const fetchAll = createAsyncThunk(
  'questionnaires/fetchAll',
  async ({ unpublished, type, limit, offset, parentsOnly }: FetchAllArgs, { rejectWithValue }) => {
    const authSession = await getAuthSession();
    const params = new URLSearchParams();
    // Legacy naming: `unpublished: true` requests only published questionnaires.
    if (unpublished) params.set('is_published', String(unpublished));
    if (type) params.set('type', type);
    if (parentsOnly) params.set('parents_only', 'true');
    params.set('limit', String(limit ?? 1000));
    if (offset) params.set('offset', String(offset));
    const url = `${baseUrl}/questionnaires/?${params.toString()}`;
    try {
      const response = await axios.get(
        url,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return (await response.data) as QuestionnaireResultArray;
    } catch (error: any) {
      // Returning the error would resolve the thunk as fulfilled and crash the
      // reducer when it reads .results — reject instead.
      return rejectWithValue(error.response?.data ?? error.message);
    }
  },
);

export const fetchQuestionnaire = createAsyncThunk(
  'questionnaires/fetchQuestionnaire',
  async (questionnaireSlug: string, { rejectWithValue }) => {
    try {
      const authSession = await getAuthSession();
      const response = await axios.get(
        `${baseUrl}/questionnaires/${questionnaireSlug}/`,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return response.data as Questionnaire;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const createQuestionnaire = createAsyncThunk(
  'questionnaires/createQuestionnaire',
  async ({
    title,
    description,
    type,
    isPublished,
    version,
    archiveDate,
    bestPracticeGuidanceLink,
    workflowLabel,
    sortOrder,
  }: FieldValues, { rejectWithValue }) => {
    try {
      const authSession = await getAuthSession();
      const response = await axios.post(
        `${baseUrl}/questionnaires/`, {
          title,
          description,
          type,
          isPublished,
          version,
          archiveDate,
          bestPracticeGuidanceLink,
          workflowLabel,
          sortOrder,
        },
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return (await response.data) as Questionnaire;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteQuestionnaire = createAsyncThunk(
  'questionnaires/deleteQuestionnaire',
  async (questionnaireId: number, { rejectWithValue }) => {
    try {
      const authSession = await getAuthSession();
      await axios.delete(
        `${baseUrl}/questionnaires/${questionnaireId}/`,
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return questionnaireId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateQuestionnaire = createAsyncThunk(
  'questionnaires/updateQuestionnaire',
  async ({
    id,
    archiveDate,
    title,
    description,
    domains,
    slug,
    isPublished,
    type,
    bestPracticeGuidanceLink,
    includeInReporting,
    workflowLabel,
    sortOrder,
  }: FieldValues, { rejectWithValue }) => {
    try {
      const authSession = await getAuthSession();
      const response = await axios.patch(
        `${baseUrl}/questionnaires/${slug}/`, {
          id,
          title,
          archiveDate,
          description,
          domains: domains.map((domain:Domain) => typeof domain === 'number' ? domain : domain.id),
          isPublished,
          type,
          bestPracticeGuidanceLink,
          includeInReporting,
          workflowLabel,
          sortOrder,
        },
        {
          headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
        },
      );
      return (await response.data) as Questionnaire;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

const questionnaireSlice = createSlice({
  name: 'questionnaires',
  initialState,
  reducers: {
    resetQuestionnaire: (state) => {
      state.questionnaire = undefined;
      state.error = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchAll.fulfilled, (state, action: PayloadAction<QuestionnaireResultArray>) => {
      state.count = action.payload.count;
      state.next = action.payload.next;
      state.previous = action.payload.previous;
      state.results = action.payload.results.sort((a, b) => a.title.localeCompare(b.title));
    });
    builder.addCase(fetchAll.rejected, (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    });
    builder.addCase(createQuestionnaire.fulfilled, (state, action: PayloadAction<Questionnaire>) => {
      state.results = [...state.results, action.payload];
      state.questionnaire = action.payload;
      state.error = null;
    });
    builder.addCase(createQuestionnaire.rejected, (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    });
    builder.addCase(deleteQuestionnaire.fulfilled, (state, action: PayloadAction<number>) => {
      state.deletedQuestionnaireId = action.payload;
      state.results = state.results.filter((questionnaire: Questionnaire) => questionnaire.id !== action.payload);
    });
    builder.addCase(updateQuestionnaire.fulfilled, (state, action: PayloadAction<Questionnaire>) => {
      state.results = state.results.filter((questionnaire: Questionnaire) => questionnaire.id !== action.payload.id);
      state.results = [...state.results, action.payload].sort((a, b) => a.id - b.id);
      state.error = null;
      state.questionnaire = action.payload;
    });
    builder.addCase(updateQuestionnaire.rejected, (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    });
    builder.addCase(fetchQuestionnaire.fulfilled, (state, action: PayloadAction<Questionnaire>) => {
      state.questionnaire = action.payload;
    });
  },
});

export const { resetQuestionnaire } = questionnaireSlice.actions;

export default questionnaireSlice.reducer;